import { useState, useEffect, useRef, useCallback } from 'react';
import { useRealtimeDoubt } from '@/hooks/chat/useRealtimeDoubt';
import { DoubtSolvingCanvas } from './DoubtSolvingCanvas';

// Create placeholder scene with centered message
// Font size scales based on container width
function createPlaceholderScene(
  containerWidth: number,
  _containerHeight: number
): any[] {
  const text = 'Press mic button to ask your doubt';

  // Scale font size based on container width (responsive)
  // Use ~3% of container width for font size, with min/max bounds
  const fontSize = Math.max(24, Math.min(72, containerWidth * 0.03));

  // Calculate required width for the text
  // Rough estimate: each character needs ~0.65 * fontSize width
  const textWidth = text.length * fontSize * 0.65;

  return [
    {
      type: 'text',
      x: 100, // Fixed position
      y: 100, // Fixed position
      width: textWidth, // Set width to accommodate full text
      text: text,
      fontSize: fontSize,
      strokeColor: '#6366f1',
    },
  ];
}

interface DoubtSessionProps {
  lessonId: string;
  currentTime: number;
  onExit: () => void;
}

export function DoubtSession({
  lessonId,
  currentTime,
  onExit,
}: DoubtSessionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [excalidrawScene, setExcalidrawScene] = useState<any[] | null>(null);
  const [isLoadingScene, setIsLoadingScene] = useState(false);
  const [, setConversationState] = useState<
    'initializing' | 'listening' | 'processing' | 'explaining' | 'waiting'
  >('initializing');

  // Create placeholder scene based on container size
  useEffect(() => {
    if (containerRef.current) {
      const { clientWidth, clientHeight } = containerRef.current;
      const placeholderScene = createPlaceholderScene(
        clientWidth,
        clientHeight
      );
      setExcalidrawScene(placeholderScene);
    }

    // Recreate scene when window resizes
    const handleResize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        const placeholderScene = createPlaceholderScene(
          clientWidth,
          clientHeight
        );
        setExcalidrawScene(placeholderScene);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Stable callbacks that don't change on every render
  const handleSceneReceived = useCallback((scene: any[]) => {
    setExcalidrawScene(scene);
    setIsLoadingScene(false);
  }, []);

  const handleNarrationStart = useCallback(() => {
    setConversationState('explaining');
  }, []);

  const handleNarrationEnd = useCallback(() => {
    setConversationState('waiting');
  }, []);

  // Capture currentTime at mount, don't make it reactive
  const connectionTimeRef = useRef(currentTime);

  const {
    connect,
    disconnect,
    isConnected,
    isPlaying,
    isRecording,
    hasPermission,
    startRecording,
    stopRecording,
    requestPermission,
  } = useRealtimeDoubt({
    lessonId,
    currentTime: connectionTimeRef.current, // Use ref value that doesn't change
    onSceneReceived: handleSceneReceived,
    onNarrationStart: handleNarrationStart,
    onNarrationEnd: handleNarrationEnd,
  });

  useEffect(() => {
    // Defer the actual connect by one tick. In React 18 StrictMode
    // dev, every effect runs mount → unmount → re-mount, which would
    // otherwise open TWO WebSockets and trigger TWO `createResponse()`
    // calls (one greeting per session). With a small timer, the first
    // mount's effect is cancelled by its cleanup before the connect
    // actually fires, so only the surviving mount opens a real
    // connection. In production (no StrictMode) this is just a
    // 50 ms warmup with no behavioral change.
    let cancelled = false;
    const startTimer = setTimeout(() => {
      if (cancelled) return;
      void connect();
    }, 50);
    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      void disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    if (isConnected) {
      setConversationState('listening');
    }
  }, [isConnected]);

  // If the server closes the WS (idle auto-close, or any disconnect),
  // tear down the doubt overlay so the underlying video reappears.
  // We track `wasConnected` so we don't fire `onExit` on the first render
  // (when `isConnected` starts false before `connect()` resolves).
  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (isConnected) {
      wasConnectedRef.current = true;
      return;
    }
    if (wasConnectedRef.current) {
      onExit();
    }
  }, [isConnected, onExit]);

  const handleCancel = () => {
    setIsLoadingScene(false);
    disconnect();
    onExit();
  };

  return (
    <div ref={containerRef} className='h-full w-full bg-card'>
      {/* Full Canvas */}
      <DoubtSolvingCanvas
        scene={excalidrawScene}
        isLoading={isLoadingScene}
        onCancel={handleCancel}
        isPlaying={isPlaying}
        isRecording={isRecording}
        hasPermission={hasPermission}
        startRecording={startRecording}
        stopRecording={stopRecording}
        requestPermission={requestPermission}
      />
    </div>
  );
}
